import { useEffect } from 'react';
import { useLanguage } from '@/i18n/LanguageContext';
import { useSiteSettings } from '@/hooks/useSiteSettings';

interface PageSEOProps {
  /** Page-specific title suffix, e.g. "Gallery" */
  titleKey?: keyof import('@/i18n/translations').Translations;
  /** Fallback title if no translation key */
  title?: string;
  /** Page-specific description key */
  descriptionKey?: keyof import('@/i18n/translations').Translations;
  /** Fallback description */
  description?: string;
  /** Canonical path, e.g. "/gallery" */
  path?: string;
  /** JSON-LD structured data object */
  jsonLd?: Record<string, any>;
}

const DOMAIN = 'https://stradivari-violins.com';

const PageSEO = ({ titleKey, title, descriptionKey, description, path = '', jsonLd }: PageSEOProps) => {
  const { t, language } = useLanguage();
  const { settings } = useSiteSettings();

  useEffect(() => {
    // Build title: "Page | Brand" or custom from admin settings
    const brandName = 'Szoradi Instruments';
    const adminTitle = language === 'de' ? settings.seo_title_de : settings.seo_title_en;
    const pageTitle = titleKey ? (t as any)[titleKey] : title;
    
    if (path === '' || path === '/') {
      // Homepage: use admin SEO title if set, otherwise translation metaTitle
      document.title = adminTitle || t.metaTitle;
    } else {
      document.title = pageTitle ? `${pageTitle} | ${brandName}` : t.metaTitle;
    }

    // Meta description
    const adminDesc = language === 'de' ? settings.seo_description_de : settings.seo_description_en;
    const pageDesc = descriptionKey ? (t as any)[descriptionKey] : description;
    const metaDesc = (path === '' || path === '/') 
      ? (adminDesc || t.metaDescription)
      : (pageDesc || t.metaDescription);
    
    const descEl = document.querySelector('meta[name="description"]');
    if (descEl) descEl.setAttribute('content', metaDesc);

    // Canonical
    const canonicalEl = document.querySelector('link[rel="canonical"]');
    if (canonicalEl) canonicalEl.setAttribute('href', `${DOMAIN}${path}`);

    // OG tags
    const setMeta = (selector: string, content: string) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute('content', content);
    };
    
    setMeta('meta[property="og:title"]', document.title);
    setMeta('meta[property="og:description"]', metaDesc);
    setMeta('meta[property="og:url"]', `${DOMAIN}${path}`);
    setMeta('meta[name="twitter:title"]', document.title);
    setMeta('meta[name="twitter:description"]', metaDesc);
    setMeta('meta[name="twitter:url"]', `${DOMAIN}${path}`);

    // HTML lang
    document.documentElement.lang = language;

    // JSON-LD for this page
    let scriptEl = document.getElementById('page-jsonld');
    if (jsonLd) {
      if (!scriptEl) {
        scriptEl = document.createElement('script');
        scriptEl.id = 'page-jsonld';
        scriptEl.setAttribute('type', 'application/ld+json');
        document.head.appendChild(scriptEl);
      }
      scriptEl.textContent = JSON.stringify(jsonLd);
    } else if (scriptEl) {
      scriptEl.remove();
    }

    return () => {
      // Cleanup page-specific JSON-LD on unmount
      const el = document.getElementById('page-jsonld');
      if (el) el.remove();
    };
  }, [language, titleKey, title, descriptionKey, description, path, jsonLd, settings, t]);

  return null;
};

export default PageSEO;
